import type { ProductRole } from '@academy/domain';
import { hasRouteCapability } from '../routes/route-access';
import { preloadWorkspaceRoute } from '../routes/route-preload';
import { workspaceRouteDescriptorForHref } from '../routes/workspace-route-descriptors';
import { primaryWorkspaceRouteMetadata } from '../routes/workspace-route-metadata';
import { createAuthorityQueryKey, prefetchQuery, type QueryCacheSource } from './query-cache';
import { persistPresentationLibrarySnapshot } from './query-snapshot-storage';
import { emitNavigationPrefetch, type NavigationPrefetchTrigger } from './navigation-telemetry';

export const maxConcurrentIntentPrefetches = 2;
export const maxConcurrentIdlePrefetches = 1;
export const maxPrefetchRequestsPerRoute = 1;
export const maxPrefetchResponseBytesPerRoute = 256 * 1024;
export const prefetchWasteWindowMs = 30_000;

export type WorkspacePrefetchAuthority = {
  accountId: number;
  activeRole: ProductRole | null;
  roles: readonly ProductRole[];
  sessionSubjectHash?: string | undefined;
  sessionGenerationHash?: string | undefined;
  onAuthoritativeDenial?: (() => void) | undefined;
};

export type WorkspacePrefetchHandle = {
  key: string | null;
  promise: Promise<void>;
  cancel: () => void;
};

type PrefetchRecord = {
  key: string;
  accountId: number;
  route: string;
  trigger: NavigationPrefetchTrigger;
  startedAt: number;
  requestCount: number;
  completed: boolean;
  consumed: boolean;
  cancelQuery: () => void;
  wasteTimer?: ReturnType<typeof setTimeout>;
  handle: WorkspacePrefetchHandle;
};

const records = new Map<string, PrefetchRecord>();
let activeIntent = 0;
let activeIdle = 0;

const now = () => (typeof performance === 'undefined' ? Date.now() : performance.now());
const elapsed = (startedAt: number) => Math.max(0, Math.round((now() - startedAt) * 10) / 10);
const resolvedHandle = (): WorkspacePrefetchHandle => ({
  key: null,
  promise: Promise.resolve(),
  cancel: () => undefined,
});

const connectionPolicy = () => {
  if (typeof navigator === 'undefined') return null;
  if (!navigator.onLine) return 'offline' as const;
  const connection = Reflect.get(navigator, 'connection') as
    { saveData?: boolean; effectiveType?: string } | undefined;
  if (connection?.saveData) return 'save-data' as const;
  if (connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') {
    return 'slow-connection' as const;
  }
  return null;
};

const isAuthoritativeAccessError = (error: unknown) => {
  if (typeof error !== 'object' || error === null) return false;
  const status: unknown = Reflect.get(error, 'status') ?? Reflect.get(error, 'statusCode');
  const code: unknown = Reflect.get(error, 'code');
  return (
    status === 401 ||
    status === 403 ||
    code === 'PGRST301' ||
    code === 'JWT_EXPIRED' ||
    code === 'BAD_JWT'
  );
};

const sourceForTrigger = (
  trigger: NavigationPrefetchTrigger,
): Extract<QueryCacheSource, 'intent-prefetch' | 'idle-prefetch' | 'navigation-handoff'> =>
  trigger === 'idle'
    ? 'idle-prefetch'
    : trigger === 'navigation'
      ? 'navigation-handoff'
      : 'intent-prefetch';

const normalizedHref = (href: string) => {
  const url = new URL(href, 'https://workspace.invalid');
  return `${url.pathname}${url.search}`;
};

export const workspacePrefetchKey = (href: string, authority: WorkspacePrefetchAuthority) => {
  const descriptor = workspaceRouteDescriptorForHref(href);
  if (!descriptor || !authority.activeRole) return null;
  const route = normalizedHref(href);
  const context = {
    href: route,
    accountId: authority.accountId,
    activeRole: authority.activeRole,
  };
  return createAuthorityQueryKey({
    accountId: authority.accountId,
    activeRole: authority.activeRole,
    route,
    query: descriptor.queryKey(context),
  });
};

export const consumeWorkspacePrefetch = (key: string) => {
  const record = records.get(key);
  if (!record || record.consumed) return null;
  record.consumed = true;
  if (record.wasteTimer) clearTimeout(record.wasteTimer);
  emitNavigationPrefetch({
    route: record.route,
    trigger: record.trigger,
    phase: record.completed ? 'hit' : 'in-flight-hit',
    elapsedMs: elapsed(record.startedAt),
    requestCount: record.requestCount,
  });
  if (record.completed) records.delete(key);
  return record.completed ? ('hit' as const) : ('in-flight-hit' as const);
};

export const beginWorkspaceRoutePrefetch = (input: {
  href: string;
  trigger: NavigationPrefetchTrigger;
  authority: WorkspacePrefetchAuthority;
}): WorkspacePrefetchHandle => {
  const descriptor = workspaceRouteDescriptorForHref(input.href);
  const route = normalizedHref(input.href);
  if (
    !descriptor ||
    !input.authority.activeRole ||
    !input.authority.roles.includes(input.authority.activeRole) ||
    !hasRouteCapability(input.authority.roles, descriptor.capability)
  ) {
    emitNavigationPrefetch({
      route,
      trigger: input.trigger,
      phase: 'skipped',
      elapsedMs: 0,
      requestCount: 0,
      skipReason: 'ineligible',
    });
    return resolvedHandle();
  }

  const modulePromise = preloadWorkspaceRoute(route).catch(() => undefined);
  const dataEligible =
    input.trigger === 'navigation'
      ? true
      : input.trigger === 'idle'
        ? descriptor.idleData
        : descriptor.intentData;
  if (!dataEligible) {
    emitNavigationPrefetch({
      route,
      trigger: input.trigger,
      phase: 'skipped',
      elapsedMs: 0,
      requestCount: 0,
      skipReason: 'ineligible',
    });
    return { key: null, promise: modulePromise.then(() => undefined), cancel: () => undefined };
  }

  const policyBlock = connectionPolicy();
  if (policyBlock) {
    emitNavigationPrefetch({
      route,
      trigger: input.trigger,
      phase: 'skipped',
      elapsedMs: 0,
      requestCount: 0,
      skipReason: policyBlock,
    });
    return { key: null, promise: modulePromise.then(() => undefined), cancel: () => undefined };
  }

  const key = workspacePrefetchKey(route, input.authority);
  if (!key) return resolvedHandle();
  const existing = records.get(key);
  if (existing) return existing.handle;

  const activeCount = input.trigger === 'idle' ? activeIdle : activeIntent;
  const concurrencyLimit =
    input.trigger === 'idle' ? maxConcurrentIdlePrefetches : maxConcurrentIntentPrefetches;
  if (input.trigger !== 'navigation' && activeCount >= concurrencyLimit) {
    emitNavigationPrefetch({
      route,
      trigger: input.trigger,
      phase: 'skipped',
      elapsedMs: 0,
      requestCount: 0,
      skipReason: 'budget',
    });
    return { key: null, promise: modulePromise.then(() => undefined), cancel: () => undefined };
  }

  const context = {
    href: route,
    accountId: input.authority.accountId,
    activeRole: input.authority.activeRole,
  };
  const startedAt = now();
  const prefetched = prefetchQuery(key, (signal) => descriptor.loadData(signal, context), {
    source: sourceForTrigger(input.trigger),
  });
  const requestCount = prefetched.status === 'started' ? 1 : 0;
  if (requestCount > 0) {
    if (input.trigger === 'idle') activeIdle += 1;
    else if (input.trigger !== 'navigation') activeIntent += 1;
  }
  emitNavigationPrefetch({
    route,
    trigger: input.trigger,
    phase: prefetched.status === 'fresh' ? 'hit' : 'start',
    elapsedMs: 0,
    requestCount,
  });

  let cancelled = false;
  const handle: WorkspacePrefetchHandle = {
    key,
    promise: Promise.resolve(),
    cancel: () => {
      const record = records.get(key);
      if (!record || record.completed || record.consumed || cancelled) return;
      cancelled = true;
      record.cancelQuery();
      records.delete(key);
      emitNavigationPrefetch({
        route,
        trigger: input.trigger,
        phase: 'cancelled',
        elapsedMs: elapsed(startedAt),
        requestCount,
      });
    },
  };
  const record: PrefetchRecord = {
    key,
    accountId: input.authority.accountId,
    route,
    trigger: input.trigger,
    startedAt,
    requestCount,
    completed: prefetched.status === 'fresh',
    consumed: prefetched.status === 'fresh',
    cancelQuery: prefetched.cancel,
    handle,
  };
  records.set(key, record);

  handle.promise = Promise.all([modulePromise, prefetched.promise])
    .then(([, data]) => {
      if (cancelled) return;
      if (descriptor.id === 'presentations') {
        persistPresentationLibrarySnapshot(data, input.authority);
      }
      record.completed = true;
      emitNavigationPrefetch({
        route,
        trigger: input.trigger,
        phase: 'completed',
        elapsedMs: elapsed(startedAt),
        requestCount,
      });
      if (record.consumed) {
        records.delete(key);
      } else {
        record.wasteTimer = setTimeout(() => {
          if (records.get(key) !== record || record.consumed) return;
          records.delete(key);
          emitNavigationPrefetch({
            route,
            trigger: input.trigger,
            phase: 'wasted',
            elapsedMs: elapsed(startedAt),
            requestCount,
          });
        }, prefetchWasteWindowMs);
      }
    })
    .catch((error: unknown) => {
      if (cancelled) return;
      records.delete(key);
      if (isAuthoritativeAccessError(error)) input.authority.onAuthoritativeDenial?.();
      emitNavigationPrefetch({
        route,
        trigger: input.trigger,
        phase: 'failed',
        elapsedMs: elapsed(startedAt),
        requestCount,
      });
    })
    .finally(() => {
      if (requestCount === 0) return;
      if (input.trigger === 'idle') activeIdle = Math.max(0, activeIdle - 1);
      else if (input.trigger !== 'navigation') activeIntent = Math.max(0, activeIntent - 1);
    });
  return handle;
};

export const scheduleIdleWorkspacePrefetch = (input: {
  allowedHrefs: readonly string[];
  authority: () => WorkspacePrefetchAuthority;
}) => {
  if (typeof window === 'undefined') return () => undefined;
  const queue = primaryWorkspaceRouteMetadata
    .filter((descriptor) => descriptor.idleCode && input.allowedHrefs.includes(descriptor.href))
    .map((descriptor) => descriptor.href);
  let cancelled = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let idleId: number | undefined;
  let activeHandle: WorkspacePrefetchHandle | null = null;
  const runNext = () => {
    if (cancelled) return;
    const href = queue.shift();
    if (!href) return;
    activeHandle = beginWorkspaceRoutePrefetch({
      href,
      trigger: 'idle',
      authority: input.authority(),
    });
    void activeHandle.promise.finally(schedule);
  };
  const schedule = () => {
    if (cancelled || queue.length === 0) return;
    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(runNext, { timeout: 2_000 });
    } else {
      timeoutId = setTimeout(runNext, 500);
    }
  };
  schedule();
  return () => {
    cancelled = true;
    activeHandle?.cancel();
    if (idleId !== undefined && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
    if (timeoutId) clearTimeout(timeoutId);
  };
};

export const clearWorkspacePrefetchRecordsForAccount = (accountId: number) => {
  for (const [key, record] of records) {
    if (record.accountId !== accountId) continue;
    if (record.wasteTimer) clearTimeout(record.wasteTimer);
    record.handle.cancel();
    records.delete(key);
  }
};

export const resetWorkspacePrefetchForTests = () => {
  for (const record of records.values()) {
    if (record.wasteTimer) clearTimeout(record.wasteTimer);
    record.handle.cancel();
  }
  records.clear();
  activeIntent = 0;
  activeIdle = 0;
};
